import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import NavigationBar from '../../components/ui/NavigationBar';
import Button from '../../components/ui/Button';
import { whiteLabelService } from '../../services/whiteLabelService';

// ─── Helpers ───────────────────────────────────────────────────────────────────
const GOOGLE_FONTS = [
  { label: 'DM Sans', value: 'DM Sans' },
  { label: 'Manrope', value: 'Manrope' },
  { label: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans' },
  { label: 'Satoshi', value: 'Satoshi' },
  { label: 'General Sans', value: 'General Sans' },
  { label: 'Cabinet Grotesk', value: 'Cabinet Grotesk' },
  { label: 'JetBrains Mono', value: 'JetBrains Mono' },
];

const StatusBadge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${ok ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}`}>
    <Icon name={ok ? 'CheckCircle2' : 'AlertCircle'} size={11} />
    {label}
  </span>
);

const SectionCard = ({ icon, title, badge = null, children }) => (
  <div className="bg-card border border-border rounded-xl overflow-hidden">
    <div className="px-5 py-4 border-b border-border flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon name={icon} size={16} color="var(--color-primary)" />
      </div>
      <h3 className="text-sm font-semibold text-foreground flex-1">{title}</h3>
      {badge}
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

const Row = ({ label, value = '', mono = false, children }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
    <span className="text-xs text-muted-foreground">{label}</span>
    {children ?? (
      <span className={`text-xs font-medium text-foreground ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-muted-foreground italic">Not set</span>}
      </span>
    )}
  </div>
);

// ─── Portal Preview ────────────────────────────────────────────────────────────
const PortalPreview = ({ settings, font }) => {
  const primary = settings?.primaryColor || '#107570';
  const logo = settings?.logoUrl;
  const agency = settings?.agencyName || 'Your Agency';
  const domain = settings?.customDomain || 'forms.youragency.com';

  return (
    <div className="rounded-xl border border-border overflow-hidden shadow-sm" style={{ fontFamily: `'${font}', sans-serif` }}>
      {/* Browser chrome */}
      <div className="bg-muted border-b border-border px-4 py-2.5 flex items-center gap-3">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-error/60" />
          <div className="w-3 h-3 rounded-full bg-warning/60" />
          <div className="w-3 h-3 rounded-full bg-success/60" />
        </div>
        <div className="flex-1 bg-background border border-border rounded-md px-3 py-1 text-xs text-muted-foreground font-mono truncate">
          https://{domain}/portal
        </div>
      </div>
      {/* Portal nav */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border" style={{ backgroundColor: primary }}>
        <div className="flex items-center gap-3">
          {logo ? (
            <img src={logo} alt="Agency logo" className="h-7 object-contain" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-white/20 flex items-center justify-center">
                <Icon name="Layers" size={14} color="white" />
              </div>
              <span className="text-white font-semibold text-sm">{agency}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-white/80 text-xs">client@example.com</span>
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
            <Icon name="User" size={13} color="white" />
          </div>
        </div>
      </div>
      {/* Portal body */}
      <div className="flex bg-background" style={{ minHeight: 220 }}>
        {/* Sidebar */}
        <div className="w-44 border-r border-border py-4 px-3 space-y-1 flex-shrink-0">
          {[
            { icon: 'LayoutDashboard', label: 'Dashboard', active: true },
            { icon: 'FileText', label: 'My Forms', active: false },
            { icon: 'Inbox', label: 'Submissions', active: false },
            { icon: 'BarChart2', label: 'Analytics', active: false },
          ]?.map((item) => (
            <div
              key={item?.label}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-smooth ${item?.active ? 'text-white' : 'text-muted-foreground'}`}
              style={item?.active ? { backgroundColor: primary } : {}}
            >
              <Icon name={item?.icon} size={13} color={item?.active ? 'white' : 'var(--color-muted-foreground)'} />
              {item?.label}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 p-5 space-y-4">
          <div>
            <p className="text-xs font-semibold text-foreground">Welcome back 👋</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Here's your portal overview</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Active Forms', value: '12', icon: 'FileText' },
              { label: 'Submissions', value: '4,820', icon: 'Inbox' },
              { label: 'Conversion', value: '68%', icon: 'TrendingUp' },
            ]?.map((stat) => (
              <div key={stat?.label} className="bg-card border border-border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] text-muted-foreground">{stat?.label}</span>
                  <Icon name={stat?.icon} size={11} color="var(--color-muted-foreground)" />
                </div>
                <p className="text-sm font-bold text-foreground">{stat?.value}</p>
              </div>
            ))}
          </div>
          <div className="bg-card border border-border rounded-lg p-3">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Recent Forms</p>
            {['Contact Form', 'Lead Capture', 'Survey Q3']?.map((name) => (
              <div key={name} className="flex items-center justify-between py-1.5 border-b border-border/40 last:border-0">
                <span className="text-xs text-foreground">{name}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: primary }}>Active</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Public Form Preview ───────────────────────────────────────────────────────
const PublicFormPreview = ({ settings, font }) => {
  const primary = settings?.primaryColor || '#107570';
  const logo = settings?.logoUrl;
  const agency = settings?.agencyName || 'Your Agency';
  const domain = settings?.customDomain || 'forms.youragency.com';

  return (
    <div className="rounded-xl border border-border overflow-hidden shadow-sm" style={{ fontFamily: `'${font}', sans-serif` }}>
      {/* Browser chrome */}
      <div className="bg-muted border-b border-border px-4 py-2.5 flex items-center gap-3">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-error/60" />
          <div className="w-3 h-3 rounded-full bg-warning/60" />
          <div className="w-3 h-3 rounded-full bg-success/60" />
        </div>
        <div className="flex-1 bg-background border border-border rounded-md px-3 py-1 text-xs text-muted-foreground font-mono truncate">
          https://{domain}/f/wf_abc123
        </div>
      </div>
      {/* Form header */}
      <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-card">
        {logo ? (
          <img src={logo} alt="Agency logo" className="h-6 object-contain" />
        ) : (
          <span className="text-sm font-semibold" style={{ color: primary }}>{agency}</span>
        )}
        <span className="text-[10px] text-muted-foreground">Powered by {agency}</span>
      </div>
      {/* Form body */}
      <div className="bg-background px-6 py-5 space-y-4">
        <div>
          <p className="text-sm font-bold text-foreground">Contact Us</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Fill out the form and we'll get back to you.</p>
        </div>
        {[
          { label: 'Full Name', placeholder: 'Jane Smith' },
          { label: 'Email Address', placeholder: 'jane@example.com' },
        ]?.map((field) => (
          <div key={field?.label} className="space-y-1">
            <label className="text-xs font-medium text-foreground">{field?.label}</label>
            <div className="w-full border border-border rounded-lg px-3 py-2 text-xs text-muted-foreground bg-card">
              {field?.placeholder}
            </div>
          </div>
        ))}
        <div className="space-y-1">
          <label className="text-xs font-medium text-foreground">Message</label>
          <div className="w-full border border-border rounded-lg px-3 py-2 text-xs text-muted-foreground bg-card h-14" />
        </div>
        <button
          className="w-full py-2.5 rounded-lg text-xs font-semibold text-white transition-smooth"
          style={{ backgroundColor: primary }}
        >
          Submit Form
        </button>
        <p className="text-center text-[10px] text-muted-foreground">
          Powered by <span style={{ color: primary }}>{agency}</span>
        </p>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const WhiteLabelPreview = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('portal'); // 'portal' | 'form'
  const [selectedFont, setSelectedFont] = useState('DM Sans');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await whiteLabelService?.getSettings();
        setSettings(data || {
          isEnabled: false,
          primaryColor: '#107570',
          agencyName: '',
          logoUrl: '',
          faviconUrl: '',
          customDomain: '',
          cnameVerified: false,
          emailFooterText: '',
        });
      } catch {
        setSettings({
          isEnabled: false,
          primaryColor: '#107570',
          agencyName: '',
          logoUrl: '',
          faviconUrl: '',
          customDomain: '',
          cnameVerified: false,
          emailFooterText: '',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copyToClipboard = (text, key) => {
    navigator?.clipboard?.writeText(text)?.then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  const primary = settings?.primaryColor || '#107570';
  const domain = settings?.customDomain || 'forms.youragency.com';

  const checks = [
    { label: 'White-label mode', ok: !!settings?.isEnabled },
    { label: 'Agency logo', ok: !!settings?.logoUrl },
    { label: 'Favicon', ok: !!settings?.faviconUrl },
    { label: 'Agency name', ok: !!settings?.agencyName },
    { label: 'Primary color', ok: !!settings?.primaryColor },
    { label: 'Custom domain', ok: !!settings?.customDomain },
    { label: 'CNAME verified', ok: !!settings?.cnameVerified },
    { label: 'Email footer', ok: !!settings?.emailFooterText },
  ];

  const completedCount = checks?.filter((c) => c?.ok)?.length;
  const completionPct = Math.round((completedCount / checks?.length) * 100);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Icon name="Loader2" size={28} color="var(--color-primary)" className="animate-spin" />
          <p className="text-sm text-muted-foreground">Loading white-label settings…</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>White-Label Preview — FormCraft</title>
        <link
          href={`https://fonts.googleapis.com/css2?family=${encodeURIComponent(selectedFont)}:wght@400;500;600;700&display=swap`}
          rel="stylesheet"
        />
      </Helmet>
      <NavigationBar stats={[]} />
      <div className="flex min-h-screen bg-background pt-16">
        {/* Sidebar spacer */}
        <div className="w-64 flex-shrink-0 hidden lg:block" />

        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-8">
          {/* Page header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Eye" size={16} color="var(--color-primary)" />
                </div>
                <h1 className="text-xl font-heading font-bold text-foreground">White-Label Preview</h1>
              </div>
              <p className="text-sm text-muted-foreground ml-10">
                Review and test all branding settings applied across your portal and public forms.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Settings"
                iconPosition="left"
                onClick={() => window.location.href = '/white-label-configuration'}
              >
                Edit Settings
              </Button>
              <Button
                variant="primary"
                size="sm"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={() => window.open(`https://${domain}/portal`, '_blank')}
              >
                Open Live Portal
              </Button>
            </div>
          </div>

          {/* Completion banner */}
          <div className="mb-6 bg-card border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="CheckSquare" size={16} color="var(--color-primary)" />
                <span className="text-sm font-semibold text-foreground">Setup Completion</span>
              </div>
              <span className="text-sm font-bold" style={{ color: primary }}>{completionPct}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 mb-3">
              <div
                className="h-2 rounded-full transition-all duration-500"
                style={{ width: `${completionPct}%`, backgroundColor: primary }}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {checks?.map((c) => (
                <StatusBadge key={c?.label} ok={c?.ok} label={c?.label} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[340px_1fr] gap-6">

            {/* ── Left column: settings review ── */}
            <div className="space-y-4">

              {/* Colors */}
              <SectionCard icon="Palette" title="Colors" badge={null}>
                <Row label="Primary color">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded border border-border flex-shrink-0" style={{ backgroundColor: primary }} />
                    <span className="text-xs font-mono text-foreground">{primary}</span>
                    <button
                      onClick={() => copyToClipboard(primary, 'color')}
                      className="text-muted-foreground hover:text-foreground transition-smooth"
                      title="Copy"
                    >
                      <Icon name={copied === 'color' ? 'Check' : 'Copy'} size={12} />
                    </button>
                  </div>
                </Row>
                <Row label="White-label mode">
                  <span className={`text-xs font-medium ${settings?.isEnabled ? 'text-success' : 'text-muted-foreground'}`}>
                    {settings?.isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </Row>
                {/* Color contrast preview */}
                <div className="mt-3 space-y-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Contrast Preview</p>
                  <div className="flex gap-2">
                    <div className="flex-1 rounded-lg py-2.5 text-center text-xs font-semibold text-white" style={{ backgroundColor: primary }}>
                      Button
                    </div>
                    <div className="flex-1 rounded-lg py-2.5 text-center text-xs font-semibold border" style={{ color: primary, borderColor: primary }}>
                      Outline
                    </div>
                    <div className="flex-1 rounded-lg py-2.5 text-center text-xs font-semibold" style={{ color: primary, backgroundColor: `${primary}18` }}>
                      Ghost
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* Fonts */}
              <SectionCard icon="Type" title="Typography" badge={null}>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1.5">Preview font</label>
                    <select
                      value={selectedFont}
                      onChange={(e) => setSelectedFont(e?.target?.value)}
                      className="w-full text-xs bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      {GOOGLE_FONTS?.map((f) => (
                        <option key={f?.value} value={f?.value}>{f?.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5 p-3 bg-muted rounded-lg" style={{ fontFamily: `'${selectedFont}', sans-serif` }}>
                    <p className="text-base font-bold text-foreground">Heading Sample</p>
                    <p className="text-sm font-medium text-foreground">Subheading text</p>
                    <p className="text-xs text-muted-foreground">Body copy — the quick brown fox jumps over the lazy dog.</p>
                    <button className="text-xs font-semibold text-white px-3 py-1.5 rounded-md" style={{ backgroundColor: primary }}>
                      Call to Action
                    </button>
                  </div>
                </div>
              </SectionCard>

              {/* Logos */}
              <SectionCard
                icon="Image"
                title="Logos & Assets"
                badge={<StatusBadge ok={!!settings?.logoUrl} label={settings?.logoUrl ? 'Uploaded' : 'Missing'} />}
              >
                <Row label="Agency name" value={settings?.agencyName} />
                <Row label="Logo">
                  {settings?.logoUrl ? (
                    <div className="flex items-center gap-2">
                      <img src={settings?.logoUrl} alt="Agency logo" className="h-7 object-contain border border-border rounded p-0.5 bg-card" />
                      <button
                        onClick={() => copyToClipboard(settings?.logoUrl, 'logo')}
                        className="text-muted-foreground hover:text-foreground transition-smooth"
                        title="Copy URL"
                      >
                        <Icon name={copied === 'logo' ? 'Check' : 'Copy'} size={12} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Not uploaded</span>
                  )}
                </Row>
                <Row label="Favicon">
                  {settings?.faviconUrl ? (
                    <div className="flex items-center gap-2">
                      <img src={settings?.faviconUrl} alt="Favicon" className="w-6 h-6 object-contain border border-border rounded p-0.5 bg-card" />
                      <button
                        onClick={() => copyToClipboard(settings?.faviconUrl, 'favicon')}
                        className="text-muted-foreground hover:text-foreground transition-smooth"
                        title="Copy URL"
                      >
                        <Icon name={copied === 'favicon' ? 'Check' : 'Copy'} size={12} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Not uploaded</span>
                  )}
                </Row>
                <Row label="Email footer" value={settings?.emailFooterText || `Powered by ${settings?.agencyName || 'Your Agency'}`} />
              </SectionCard>

              {/* Domain */}
              <SectionCard
                icon="Globe"
                title="Domain Settings"
                badge={<StatusBadge ok={!!settings?.cnameVerified} label={settings?.cnameVerified ? 'Verified' : 'Pending'} />}
              >
                <Row label="Custom domain" value={settings?.customDomain} mono />
                <Row label="CNAME status">
                  <span className={`text-xs font-medium ${settings?.cnameVerified ? 'text-success' : 'text-warning'}`}>
                    {settings?.cnameVerified ? 'Verified' : 'Pending verification'}
                  </span>
                </Row>
                <Row label="SSL">
                  <span className={`text-xs font-medium ${settings?.cnameVerified ? 'text-success' : 'text-muted-foreground'}`}>
                    {settings?.cnameVerified ? 'Active' : 'Pending CNAME'}
                  </span>
                </Row>
                {settings?.customDomain && (
                  <div className="mt-3 space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Quick Links</p>
                    {[
                      { label: 'Portal URL', url: `https://${domain}/portal` },
                      { label: 'Form embed base', url: `https://${domain}/f/` },
                    ]?.map((link) => (
                      <div key={link?.label} className="flex items-center justify-between gap-2 bg-muted rounded-lg px-3 py-2">
                        <span className="text-[10px] text-muted-foreground">{link?.label}</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-mono text-foreground truncate max-w-[120px]">{link?.url}</span>
                          <button
                            onClick={() => copyToClipboard(link?.url, link?.label)}
                            className="text-muted-foreground hover:text-foreground transition-smooth flex-shrink-0"
                          >
                            <Icon name={copied === link?.label ? 'Check' : 'Copy'} size={11} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>

            {/* ── Right column: live preview ── */}
            <div className="space-y-4">
              {/* View toggle */}
              <div className="flex items-center gap-1 bg-muted p-1 rounded-xl w-fit">
                {[
                  { key: 'portal', label: 'Customer Portal', icon: 'LayoutDashboard' },
                  { key: 'form', label: 'Public Form', icon: 'FileText' },
                ]?.map((tab) => (
                  <button
                    key={tab?.key}
                    onClick={() => setActiveView(tab?.key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-smooth ${
                      activeView === tab?.key
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={13} color={activeView === tab?.key ? 'var(--color-foreground)' : 'var(--color-muted-foreground)'} />
                    {tab?.label}
                  </button>
                ))}
              </div>

              {/* Preview label */}
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="text-xs text-muted-foreground">
                  Live preview — reflects your current white-label settings
                </span>
              </div>

              {/* Preview panel */}
              {activeView === 'portal' ? (
                <PortalPreview settings={settings} font={selectedFont} />
              ) : (
                <PublicFormPreview settings={settings} font={selectedFont} />
              )}

              {/* Embed code preview */}
              <SectionCard icon="Code2" title="Embed Code Preview">
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    When white-label mode is active, your clients receive embed codes using your custom domain.
                  </p>
                  <div className="bg-muted rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                      <span className="text-[10px] font-mono font-semibold text-yellow-500 uppercase">JavaScript Embed</span>
                      <button
                        onClick={() => copyToClipboard(`<script src="https://${domain}/embed.js"></script>\n<script>\n  Forms.embed({ formId: 'wf_abc123', container: '#form-container' });\n</script>\n<div id="form-container"></div>`, 'embed-js')}
                        className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-smooth"
                      >
                        <Icon name={copied === 'embed-js' ? 'Check' : 'Copy'} size={11} />
                        {copied === 'embed-js' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <pre className="p-4 text-xs font-mono text-foreground overflow-x-auto whitespace-pre">{`<script src="https://${domain}/embed.js"></script>
<script>
  Forms.embed({
    formId: 'wf_abc123',
    container: '#form-container',
  });
</script>
<div id="form-container"></div>`}</pre>
                  </div>
                  <div className="bg-muted rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                      <span className="text-[10px] font-mono font-semibold text-orange-500 uppercase">iFrame Embed</span>
                      <button
                        onClick={() => copyToClipboard(`<iframe src="https://${domain}/f/wf_abc123" width="100%" height="600" frameborder="0" style="border:none;border-radius:8px;" title="Contact Form"></iframe>`, 'embed-iframe')}
                        className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-smooth"
                      >
                        <Icon name={copied === 'embed-iframe' ? 'Check' : 'Copy'} size={11} />
                        {copied === 'embed-iframe' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <pre className="p-4 text-xs font-mono text-foreground overflow-x-auto whitespace-pre">{`<iframe
  src="https://${domain}/f/wf_abc123"
  width="100%"
  height="600"
  frameborder="0"
  style="border: none; border-radius: 8px;"
  title="Contact Form">
</iframe>`}</pre>
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default WhiteLabelPreview;
